package com.siriusxm.cmc.views

import androidx.compose.runtime.*
import com.siriusxm.cmc.utils.ResizeParams
import com.siriusxm.cmc.utils.encodeURIComponent
import com.siriusxm.pia.Application
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.Badge
import com.siriusxm.pia.components.BadgeColor
import com.siriusxm.pia.components.SVGIcons
import com.siriusxm.pia.components.shadowBorder
import com.siriusxm.pia.components.svgIcon
import com.siriusxm.pia.rest.unifiedaggregator.Entity
import com.siriusxm.pia.rest.unifiedaggregator.asEntities
import com.siriusxm.pia.views.unifiedaggregator.resizedUrl
import unifiedcontent.aggregator.Aggregator
import unifiedcontent.aggregator.EntityTypeConfiguration
import io.ktor.client.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.delay
import kotlinx.serialization.json.Json
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * A filter token parsed from the search input.
 * For example, "type:show" becomes FilterToken("type", "show")
 */
private data class FilterToken(
    val key: String,
    val value: String
)

/**
 * Parsed search input containing the query text and any filter tokens.
 */
private data class ParsedSearch(
    val query: String,
    val type: String? = null,
    val visibility: Boolean? = null
)

/**
 * Styles for the smart search input component.
 */
private object SmartSearchStyles : StyleSheet() {
    val container by style {
        position(Position.Relative)
    }

    val inputRow by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(12.px)
    }

    // The box that wraps any active filter chips and the text field, so chips render
    // inline to the left of where the user types. Mirrors the app's default text-input
    // look (rounded, shadow border, control surface) since the inner input is stripped
    // of its own chrome to avoid a double border.
    val inputBox by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        flexWrap(FlexWrap.Wrap)
        gap(6.px)
        width(100.percent)
        minHeight(2.5.cssRem)
        padding((0.2).cssRem, (0.6).cssRem)
        borderRadius(10.px)
        shadowBorder()
        border(1.px, LineStyle.Solid, Color.transparent)
        backgroundColor(SXMUI.selectionControlSurface.value())
        property("box-sizing", "border-box")
    }

    // An inline, removable filter chip (e.g. the selected entity type).
    val filterChip by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(4.px)
        property("flex", "0 0 auto")
        padding(2.px, 6.px)
        fontSize(12.px)
        fontWeight(500)
        borderRadius(4.px)
        backgroundColor(Color(BadgeColor.BLUE.background))
        color(Color(BadgeColor.BLUE.text))
        border(1.px, LineStyle.Solid, Color(BadgeColor.BLUE.border))
        whiteSpace("nowrap")
    }

    // The circular Filter icon button. Clicking it reveals the entity-type and visibility
    // selects inline (the icon is replaced by the selects). Styled as a neutral,
    // control-surface circle to distinguish it from the primary submit action.
    val filterButton by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        property("flex", "0 0 auto")
        width(2.5.cssRem)
        height(2.5.cssRem)
        padding(0.px)
        backgroundColor(SXMUI.selectionControlSurface.value())
        border(1.px, LineStyle.Solid, SXMUI.borderDividerDefault.value())
        borderRadius(50.percent)
        cursor("pointer")
        color(SXMUI.textColor.value())
        property("box-sizing", "border-box")
    }

    // The inline container that replaces the filter icon once expanded, holding the
    // entity-type and visibility selects side by side.
    val inlineFilters by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(6.px)
        property("flex", "0 0 auto")
    }

    // A single select shown inline in the search row when filters are expanded.
    // Matches the input box's height (2.5rem) and corner radius (10px) so the expanded
    // filters sit flush with the search field rather than looking slightly taller or
    // more/less rounded. box-sizing: border-box keeps the border inside the 2.5rem.
    val filterSelect by style {
        height(2.5.cssRem)
        padding(0.px, 8.px)
        borderRadius(10.px)
        border(1.px, LineStyle.Solid, SXMUI.borderDividerDefault.value())
        backgroundColor(SXMUI.selectionControlSurface.value())
        color(SXMUI.textColor.value())
        fontSize(13.px)
        cursor("pointer")
        property("box-sizing", "border-box")
    }

    val dropdown by style {
        position(Position.Absolute)
        top(100.percent)
        left(0.px)
        right(0.px)
        marginTop(4.px)
        backgroundColor(SXMUI.containerContentBackgroundColor.value())
        border(1.px, LineStyle.Solid, SXMUI.borderDividerDefault.value())
        borderRadius(4.px)
        property("box-shadow", "0 4px 6px rgba(0,0,0,0.1)")
        property("z-index", "1000")
        maxHeight(300.px)
        overflow("auto")
    }

    val suggestionItem by style {
        padding(8.px, 12.px)
        cursor("pointer")

        self + hover style {
            backgroundColor(SXMUI.selectionBackgroundColor.value())
        }
    }

    val suggestionItemSelected by style {
        backgroundColor(SXMUI.selectionBackgroundColor.value())
    }

    val suggestionCategory by style {
        padding(4.px, 12.px)
        fontSize(11.px)
        color(SXMUI.subtleTextColor.value())
        property("text-transform", "uppercase")
        backgroundColor(SXMUI.containerHeaderBackgroundColor.value())
    }

    // A single inline entity result row (thumbnail + label + type badge).
    val resultItem by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(12.px)
        padding(8.px, 12.px)
        cursor("pointer")

        self + hover style {
            backgroundColor(SXMUI.selectionBackgroundColor.value())
        }
    }

    val resultItemSelected by style {
        backgroundColor(SXMUI.selectionBackgroundColor.value())
    }

    // Fixed-size square thumbnail container for an inline result.
    val resultThumbnail by style {
        property("flex", "0 0 auto")
        width(36.px)
        height(36.px)
        borderRadius(4.px)
        overflow("hidden")
        backgroundColor(SXMUI.containerHeaderBackgroundColor.value())
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
    }

    val resultLabel by style {
        overflow("hidden")
        property("text-overflow", "ellipsis")
        property("white-space", "nowrap")
    }

    // A subtle status line shown while searching or when there are no matches.
    val resultStatus by style {
        padding(8.px, 12.px)
        fontSize(13.px)
        color(SXMUI.subtleTextColor.value())
    }
}

/**
 * A smart search input with autocomplete support for filter tokens.
 * Supports `type:<entity-type>` and `visibility:hidden|visible` filters.
 *
 * When the user types `type:` or `visibility:`, a dropdown appears with suggestions.
 * The final search navigates to the aggregator search page with appropriate query parameters.
 *
 * The [initialQuery], [initialTypes], and [initialVisibility] parameters seed the editable
 * state so the field and filter chips can reflect a search carried in the URL (e.g. the
 * aggregator home's `aggregator/search?query=…&type=…&visible=…` deep link). Each seed keys
 * its `remember`, so the state re-syncs whenever navigation changes a seed value but stays
 * untouched while the user is typing (the seeds are stable between navigations).
 *
 * @param initialQuery free-text query to pre-fill the input with
 * @param initialTypes entity-type ids to pre-populate as removable filter chips
 * @param initialVisibility visibility filter to pre-populate (true = visible, false = hidden,
 *   null = no filter)
 */
@Composable
fun SmartSearchInput(
    initialQuery: String = "",
    initialTypes: List<String> = emptyList(),
    initialVisibility: Boolean? = null
) {
    Style(SmartSearchStyles)

    var inputValue by remember(initialQuery) { mutableStateOf(initialQuery) }
    var entityTypes by remember { mutableStateOf<List<EntityTypeConfiguration>>(emptyList()) }
    var showDropdown by remember { mutableStateOf(false) }
    var selectedIndex by remember { mutableStateOf(0) }
    var isLoadingTypes by remember { mutableStateOf(true) }

    // The active entity-type filters (entity type ids), held separately from the text so
    // they render as inline, removable chips rather than `type:<id>` tokens. Multiple
    // types are supported and sent to the API as a comma-separated list.
    var selectedTypes by remember(initialTypes) { mutableStateOf(initialTypes) }

    // The active visibility filter (true = visible, false = hidden, null = no filter),
    // held separately from the text so it renders as an inline, removable chip.
    var selectedVisibility by remember(initialVisibility) { mutableStateOf(initialVisibility) }

    // Whether the inline filter controls (entity-type + visibility selects) are expanded.
    // Collapsed, a single Filter icon is shown; clicking it replaces the icon with the two
    // selects inline. They drive the same selectedTypes / selectedVisibility state as the
    // chips, so everything stays in sync — an explicit, discoverable alternative to typing
    // `type:`/`visibility:` tokens.
    var showFilters by remember { mutableStateOf(false) }

    // The aggregator client is created once on mount and reused for both entity-type
    // loading and live lookahead entity search.
    var aggregator by remember { mutableStateOf<Aggregator?>(null) }

    // Live lookahead entity results shown inline as the user types free-text.
    var entityResults by remember { mutableStateOf<List<Entity>>(emptyList()) }
    var isSearching by remember { mutableStateOf(false) }
    // Whether the input currently has focus; the inline results dropdown only shows
    // while focused so it disappears when the user clicks away.
    var inputFocused by remember { mutableStateOf(false) }
    // Lookup of entity type id -> human-readable display name, used to label results.
    val entityTypeNames = remember(entityTypes) {
        entityTypes.associate { it.type to it.name.ifBlank { it.type } }
    }

    // Load entity types on mount
    LaunchedEffect(Unit) {
        try {
            val httpClient = HttpClient {
                install(ContentNegotiation) {
                    json(Json {
                        isLenient = true
                        ignoreUnknownKeys = true
                    })
                }
                Application.context.installAuth(this)
            }
            val client = Aggregator(Application.configuration.unifiedAggregatorApiUrl, httpClient)
            aggregator = client
            entityTypes = client.getEntityTypes()
                .filter { it.searchable }
                .sortedBy { it.name.ifBlank { it.type } }
        } catch (e: Exception) {
            console.error("Failed to load entity types", e)
        } finally {
            isLoadingTypes = false
        }
    }

    // Determine what suggestions to show based on current input
    val suggestions = remember(inputValue, entityTypes) {
        getSuggestions(inputValue, entityTypes)
    }

    // Update dropdown visibility and reset selection when suggestions change
    LaunchedEffect(suggestions) {
        showDropdown = suggestions.isNotEmpty()
        selectedIndex = 0
    }

    // Debounced live lookahead search. Runs whenever the input, the active filters,
    // or the loaded client changes. Because LaunchedEffect cancels the previous coroutine
    // on each change, the delay() below both debounces keystrokes and cancels stale
    // in-flight requests.
    LaunchedEffect(inputValue, selectedTypes, selectedVisibility, aggregator) {
        val client = aggregator
        val parsed = parseSearchInput(inputValue)

        // Don't show entity results while the user is composing a filter token
        // (type:/visibility:) — the filter-suggestion dropdown takes over instead.
        val composingToken = getSuggestions(inputValue, entityTypes).isNotEmpty()

        if (client == null || composingToken || parsed.query.isBlank()) {
            entityResults = emptyList()
            isSearching = false
            return@LaunchedEffect
        }

        isSearching = true
        delay(250)
        try {
            val response = client.searchEntities(
                query = parsed.query,
                types = combinedTypes(selectedTypes, parsed.type),
                size = 8,
                visible = selectedVisibility ?: parsed.visibility
            )
            entityResults = response.entities.asEntities()
            isSearching = false
        } catch (e: CancellationException) {
            // The input changed and this search was superseded; leave state untouched
            // so the newer search's results/loading flag aren't clobbered.
            throw e
        } catch (e: Exception) {
            console.error("Lookahead search failed", e)
            entityResults = emptyList()
            isSearching = false
        }
    }

    fun executeSearch() {
        val parsed = parseSearchInput(inputValue)
        // The destination search page only fetches results when it has query text, so
        // require a non-blank query here; filter chips alone don't trigger a search.
        if (parsed.query.isBlank()) return

        val types = combinedTypes(selectedTypes, parsed.type)
        val visibility = selectedVisibility ?: parsed.visibility

        val params = mutableListOf("query=${encodeURIComponent(parsed.query)}")
        types?.let { params.add("type=${encodeURIComponent(it)}") }
        visibility?.let { params.add("visible=$it") }

        Application.navigate("aggregator/search?${params.joinToString("&")}")
    }

    fun applySuggestion(suggestion: Suggestion) {
        when (suggestion.tokenKey) {
            // Selecting a type/visibility value promotes it to an inline chip and strips
            // the partial token the user was typing out of the text field.
            "type" -> {
                if (suggestion.value !in selectedTypes) {
                    selectedTypes = selectedTypes + suggestion.value
                }
                inputValue = removeLastWord(inputValue)
            }
            "visibility" -> {
                selectedVisibility = suggestion.value == "visible"
                inputValue = removeLastWord(inputValue)
            }
            else -> {
                inputValue = applyTokenToInput(inputValue, suggestion)
            }
        }
        showDropdown = false
    }

    fun openEntity(entity: Entity) {
        Application.navigate("aggregator/entity/${encodeURIComponent(entity.id)}")
    }

    Div({ classes(SmartSearchStyles.container) }) {
        Div({ classes(SmartSearchStyles.inputRow) }) {
            Div({ style { flexGrow(1); position(Position.Relative) } }) {
                Div({ classes(SmartSearchStyles.inputBox) }) {
                    // Inline chips for the active type filters, each showing the type's
                    // display name (not its id). Clicking a chip's X removes that type.
                    selectedTypes.forEach { typeId ->
                        Div({ classes(SmartSearchStyles.filterChip) }) {
                            Text(entityTypeNames[typeId] ?: typeId)
                            svgIcon(SVGIcons.xMark) {
                                size = 14.px
                                title = "Remove filter"
                                action { selectedTypes = selectedTypes - typeId }
                            }
                        }
                    }

                    // Inline chip for the active visibility filter. Clicking the X removes it.
                    selectedVisibility?.let { visible ->
                        Div({ classes(SmartSearchStyles.filterChip) }) {
                            Text(if (visible) "Visible" else "Hidden")
                            svgIcon(SVGIcons.xMark) {
                                size = 14.px
                                title = "Remove filter"
                                action { selectedVisibility = null }
                            }
                        }
                    }

                    Input(InputType.Text) {
                        // Inline styles (not a class) so they override the app's global
                        // input[type=text] rules; the wrapping box owns the visible chrome.
                        style {
                            flexGrow(1)
                            minWidth(80.px)
                            height(2.em)
                            border(0.px)
                            property("outline", "none")
                            property("box-shadow", "none")
                            property("background-color", "transparent")
                            fontSize(14.px)
                            padding(0.px)
                            color(SXMUI.textColor.value())
                        }
                        placeholder(
                            if (selectedTypes.isNotEmpty() || selectedVisibility != null) "Search entities..."
                            else "Search entities... (try type: or visibility:)"
                        )
                        value(inputValue)
                        onInput {
                            inputValue = it.value
                        }
                        onKeyDown { event ->
                            when (event.key) {
                                "Enter" -> {
                                    if (showDropdown && suggestions.isNotEmpty()) {
                                        applySuggestion(suggestions[selectedIndex])
                                    } else {
                                        executeSearch()
                                    }
                                    event.preventDefault()
                                }
                                "ArrowDown" -> {
                                    if (showDropdown && suggestions.isNotEmpty()) {
                                        selectedIndex = (selectedIndex + 1) % suggestions.size
                                        event.preventDefault()
                                    }
                                }
                                "ArrowUp" -> {
                                    if (showDropdown && suggestions.isNotEmpty()) {
                                        selectedIndex = if (selectedIndex > 0) selectedIndex - 1 else suggestions.size - 1
                                        event.preventDefault()
                                    }
                                }
                                "Backspace" -> {
                                    // Backspacing into an empty field removes the last
                                    // filter chip (visibility renders rightmost, so it
                                    // goes first, then the most recently added type).
                                    if (inputValue.isEmpty()) {
                                        when {
                                            selectedVisibility != null -> {
                                                selectedVisibility = null
                                                event.preventDefault()
                                            }
                                            selectedTypes.isNotEmpty() -> {
                                                selectedTypes = selectedTypes.dropLast(1)
                                                event.preventDefault()
                                            }
                                        }
                                    }
                                }
                                "Escape" -> {
                                    showDropdown = false
                                }
                            }
                        }
                        onFocusIn {
                            inputFocused = true
                        }
                        onFocusOut {
                            // Delay hiding to allow click on a suggestion or result
                            kotlinx.browser.window.setTimeout({
                                showDropdown = false
                                inputFocused = false
                            }, 200)
                        }
                    }
                }

                // Filter-token suggestions take priority while the user is composing a
                // `type:`/`visibility:` token.
                if (showDropdown && suggestions.isNotEmpty()) {
                    Div({ classes(SmartSearchStyles.dropdown) }) {
                        suggestions.forEachIndexed { index, suggestion ->
                            Div({
                                classes(SmartSearchStyles.suggestionItem)
                                if (index == selectedIndex) {
                                    classes(SmartSearchStyles.suggestionItemSelected)
                                }
                                onClick {
                                    applySuggestion(suggestion)
                                }
                            }) {
                                Text(suggestion.display)
                            }
                        }
                    }
                } else if (inputFocused && parseSearchInput(inputValue).query.isNotBlank()) {
                    // Otherwise, show live lookahead entity results inline.
                    EntityResultsDropdown(entityResults, isSearching, entityTypeNames) { entity ->
                        openEntity(entity)
                    }
                }
            }

            // The Filter control. Collapsed, it's a single icon button; clicking it replaces
            // the icon with two inline selects (entity type + visibility) that mirror the
            // previous search's controls. The selects update the same chip-backed state
            // (selectedTypes / selectedVisibility), so the chips and the selects stay
            // consistent.
            if (!showFilters) {
                Div({
                    classes(SmartSearchStyles.filterButton)
                    onClick { showFilters = true }
                }) {
                    svgIcon(SVGIcons.filter) {
                        size = 20.px
                        title = "Filter"
                    }
                }
            } else {
                Div({ classes(SmartSearchStyles.inlineFilters) }) {
                    // Entity type. Selecting a real type appends it to the active type set
                    // (matching how `type:` tokens accumulate); removal is via the chip X, so
                    // this select always shows "All Types" as its resting option.
                    Select({
                        classes(SmartSearchStyles.filterSelect)
                        onChange { event ->
                            val type = event.value?.takeIf { it != "any" }
                            if (type != null && type !in selectedTypes) {
                                selectedTypes = selectedTypes + type
                            }
                        }
                    }) {
                        Option("any", { selected() }) {
                            Text("All Types")
                        }
                        entityTypes.forEach { type ->
                            Option(type.type) {
                                Text(type.name.ifBlank { type.type })
                            }
                        }
                    }

                    // Visibility maps directly to the single selectedVisibility value.
                    Select({
                        classes(SmartSearchStyles.filterSelect)
                        onChange { event ->
                            selectedVisibility = when (event.value) {
                                "visible" -> true
                                "hidden" -> false
                                else -> null
                            }
                        }
                    }) {
                        Option("any", {
                            if (selectedVisibility == null) selected()
                        }) {
                            Text("All Visibility")
                        }
                        Option("visible", {
                            if (selectedVisibility == true) selected()
                        }) {
                            Text("Visible")
                        }
                        Option("hidden", {
                            if (selectedVisibility == false) selected()
                        }) {
                            Text("Hidden")
                        }
                    }
                }
            }

            Button({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    width(40.px)
                    height(40.px)
                    padding(0.px)
                    backgroundColor(SXMUI.buttonBackgroundPrimary.value())
                    border(0.px)
                    borderRadius(50.percent)
                    cursor("pointer")
                    color(SXMUI.buttonForegroundPrimary.value())
                }
                onClick { executeSearch() }
            }) {
                svgIcon(SVGIcons.arrowRight) {
                    size = 20.px
                }
            }
        }
    }
}

/**
 * Renders the inline lookahead results dropdown for the smart search input.
 *
 * Shows a "Searching…" hint while a request is in flight, a "No matches" hint when a
 * completed search returned nothing, and otherwise a compact list of matching entities.
 * Each row shows a small thumbnail, the entity label, and a type badge; clicking a row
 * invokes [onSelect] with that entity.
 *
 * @param results the entities to display
 * @param isSearching whether a lookahead request is currently in flight
 * @param typeNames map of entity type id to its human-readable display name
 * @param onSelect callback invoked when the user clicks a result
 */
@Composable
private fun EntityResultsDropdown(
    results: List<Entity>,
    isSearching: Boolean,
    typeNames: Map<String, String>,
    onSelect: (Entity) -> Unit
) {
    // Nothing to show yet: no results and not actively searching.
    if (results.isEmpty() && !isSearching) return

    Div({ classes(SmartSearchStyles.dropdown) }) {
        when {
            results.isNotEmpty() -> {
                results.forEach { entity ->
                    Div({
                        classes(SmartSearchStyles.resultItem)
                        onClick { onSelect(entity) }
                    }) {
                        Div({ classes(SmartSearchStyles.resultThumbnail) }) {
                            val imageUrl = entity.thumbnail?.let {
                                it.resizedUrl(
                                    ResizeParams.constrainToWidth(36, it.width, it.height)
                                )
                            }
                            if (imageUrl != null) {
                                Img(src = imageUrl) {
                                    style {
                                        width(100.percent)
                                        height(100.percent)
                                        property("object-fit", "cover")
                                        display(DisplayStyle.Block)
                                    }
                                }
                            }
                        }
                        Div({
                            classes(SmartSearchStyles.resultLabel)
                            style { flexGrow(1) }
                        }) {
                            Span({ style { fontWeight(600) } }) {
                                Text(entity.label)
                            }
                        }
                        Badge(typeNames[entity.type] ?: entity.type, BadgeColor.BLUE)
                    }
                }
            }

            isSearching -> {
                Div({ classes(SmartSearchStyles.resultStatus) }) {
                    Text("Searching…")
                }
            }
        }
    }
}

/**
 * Represents a suggestion shown in the autocomplete dropdown.
 */
private data class Suggestion(
    val tokenKey: String,
    val value: String,
    val display: String
)

/**
 * Determines what suggestions to show based on the current input.
 * Detects partial tokens like "type:" or "visibility:" and provides completions.
 */
private fun getSuggestions(input: String, entityTypes: List<EntityTypeConfiguration>): List<Suggestion> {
    // Check if we're currently typing a token
    val lastWord = input.split(" ").lastOrNull() ?: return emptyList()

    // Check for type: prefix
    if (lastWord.startsWith("type:", ignoreCase = true)) {
        val partial = lastWord.substringAfter(":", "").lowercase()
        return entityTypes
            .filter { type ->
                partial.isEmpty() ||
                type.type.lowercase().contains(partial) ||
                type.name.lowercase().contains(partial)
            }
            .take(15)
            .map { Suggestion("type", it.type, "type:${it.type} (${it.name})") }
    }

    // Check for visibility: prefix
    if (lastWord.startsWith("visibility:", ignoreCase = true)) {
        val partial = lastWord.substringAfter(":", "").lowercase()
        val options = listOf(
            Suggestion("visibility", "visible", "visibility:visible"),
            Suggestion("visibility", "hidden", "visibility:hidden")
        )
        return options.filter { partial.isEmpty() || it.value.startsWith(partial) }
    }

    // Check for partial token prefixes (show hint to complete)
    if ("type:".startsWith(lastWord.lowercase()) && lastWord.isNotEmpty() && lastWord != "type:") {
        return listOf(Suggestion("hint", "type:", "type: (filter by entity type)"))
    }
    if ("visibility:".startsWith(lastWord.lowercase()) && lastWord.isNotEmpty() && lastWord != "visibility:") {
        return listOf(Suggestion("hint", "visibility:", "visibility: (filter by visibility)"))
    }

    return emptyList()
}

/**
 * Combines the chip-selected type ids with any type still typed inline as a `type:` token,
 * de-duplicated, into the comma-separated string the search API expects. Returns null when
 * no type filter is active.
 */
private fun combinedTypes(selectedTypes: List<String>, typedType: String?): String? {
    val all = (selectedTypes + listOfNotNull(typedType?.takeIf { it.isNotBlank() })).distinct()
    return all.takeIf { it.isNotEmpty() }?.joinToString(",")
}

/**
 * Removes the last whitespace-delimited word from [input], preserving a single trailing
 * space so the user can keep typing query text. Used when a partial `type:` token is
 * promoted to the inline type chip.
 */
private fun removeLastWord(input: String): String {
    val trimmedEnd = input.trimEnd()
    val lastSpace = trimmedEnd.lastIndexOf(' ')
    return if (lastSpace < 0) "" else trimmedEnd.substring(0, lastSpace + 1)
}

/**
 * Applies a selected suggestion to the input, replacing the partial token.
 */
private fun applyTokenToInput(input: String, suggestion: Suggestion): String {
    val words = input.split(" ").toMutableList()
    if (words.isEmpty()) return "${suggestion.tokenKey}:${suggestion.value} "

    // Handle hint suggestions (just complete the token prefix)
    if (suggestion.tokenKey == "hint") {
        words[words.lastIndex] = suggestion.value
        return words.joinToString(" ")
    }

    // Replace the last word with the complete token
    words[words.lastIndex] = "${suggestion.tokenKey}:${suggestion.value}"
    return words.joinToString(" ") + " "
}

/**
 * Parses the search input to extract filter tokens and the remaining query text.
 */
private fun parseSearchInput(input: String): ParsedSearch {
    val words = input.trim().split(" ")
    val queryParts = mutableListOf<String>()
    var type: String? = null
    var visibility: Boolean? = null

    for (word in words) {
        when {
            word.startsWith("type:", ignoreCase = true) -> {
                type = word.substringAfter(":")
            }
            word.startsWith("visibility:", ignoreCase = true) -> {
                val value = word.substringAfter(":").lowercase()
                visibility = when (value) {
                    "visible" -> true
                    "hidden" -> false
                    else -> null
                }
            }
            else -> {
                if (word.isNotBlank()) {
                    queryParts.add(word)
                }
            }
        }
    }

    return ParsedSearch(
        query = queryParts.joinToString(" "),
        type = type,
        visibility = visibility
    )
}
